import { ModalStyleEnum } from '@/shared/enums/Modal';
import { PreviousModalType } from '@/shared/types/Compounds/Modal';
import { isNull } from 'lodash';
import React, { useState, createContext, ReactNode, useRef } from 'react';

export type ModalContextType = {
    isModalOpen : boolean;
    openModal : () => void;
    closeModal : (shouldReset ?: boolean) => void;
    modalView : ReactNode;
    setModalView : (modalView : null | ReactNode, modalStyle ?: null | ModalStyleEnum, modalClassName ?: null | string, modalInnerClassName ?: null | string) => void;
    openPreviousModal : () => void;
    modalStyle : null | ModalStyleEnum;
    modalClassName : null | string;
    modalInnerClassName : null | string;
    modalTitle : string;
    setModalTitle : (title : string) => void;
    isModalLocked : boolean;
    setModalLocked : (locked : boolean) => void;
};

export const ModalContext = createContext<ModalContextType>({} as ModalContextType);

export type ModalContextProviderType = {
    children : ReactNode;
};

export const ModalContextProvider : React.FC<ModalContextProviderType> = ({ children }) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalView, setModalView] = useState<ReactNode>(null);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [modalStyle, setModalStyle] = useState<null | ModalStyleEnum>(null);
    const [modalClassName, setModalClassName] = useState<null | string>(null);
    const [modalInnerClassName, setModalInnerClassName] = useState<null | string>(null);
    const [isModalLocked, setModalLocked] = useState<boolean>(false);

    const previousModal = useRef<null | PreviousModalType>(null);

    const openModal = () : void => {
        setIsModalOpen(true);
    };

    const closeModal = (shouldReset ?: boolean) : void => {
        setIsModalOpen(false);

        if (shouldReset) {
            reset();
        }
    };

    const setModalViewHandler = (modalView : null | ReactNode, modalStyle : null | ModalStyleEnum = null, modalClassName : null | string = null, modalInnerClassName : null | string = null) : void => {
        setModalView(modalView);
        setModalStyle(modalStyle);
        setModalClassName(modalClassName);
        setModalInnerClassName(modalInnerClassName);

        if (!isNull(previousModal.current)) {
            return;
        }

        previousModal.current = { modalView, modalStyle, modalClassName, modalInnerClassName };
    };

    const openPreviousModal = () : void => {
        if (isModalOpen) {
            closeModal();
        }

        if (isNull(previousModal.current)) {
            reset();
            closeModal();

            return;
        }

        const { modalView, modalStyle, modalClassName, modalInnerClassName } = previousModal.current;

        setModalViewHandler(modalView, modalStyle, modalClassName, modalInnerClassName);
        openModal();
    };

    const reset = () : void => {
        setModalViewHandler(null, null, null);
        previousModal.current = null;
    };

    return (
        <ModalContext.Provider value={{
            isModalOpen,
            openModal,
            closeModal,
            modalView,
            setModalView: setModalViewHandler,
            openPreviousModal,
            modalStyle,
            modalClassName,
            modalInnerClassName,
            modalTitle,
            setModalTitle,
            isModalLocked,
            setModalLocked,
        }}>
            {children}
        </ModalContext.Provider>
    );
};
